import React, { useEffect, useState } from "react";
import { AlertCircle, Building2, Link as LinkIcon, Save, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import SuccessToast from "../components/SuccessToast";
import {
  DEFAULT_PLATFORM_SETTINGS,
  loadPlatformSettings,
  savePlatformSettings,
  validatePlatformSettings,
} from "../lib/platformSettings";
import { getAdminUser } from "./adminAuth";
import AdminTopBar from "./AdminTopBar";

const FieldLabel = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-900 mb-1.5">{children}</label>
);

const inputClass =
  "h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-maroon";
const readOnlyClass =
  "h-10 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-700";

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    organizationName: DEFAULT_PLATFORM_SETTINGS.organizationName,
    organizationEmail: DEFAULT_PLATFORM_SETTINGS.organizationEmail,
    organizationPhone: DEFAULT_PLATFORM_SETTINGS.organizationPhone,
    organizationWebsite: DEFAULT_PLATFORM_SETTINGS.organizationWebsite,
    facebookUrl: "",
    instagramUrl: "",
    maintenanceMode: false,
  });

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const settings = await loadPlatformSettings();
        if (!isMounted) return;
        setForm(settings);
      } catch (error) {
        console.error("Failed to load platform settings:", error);
        if (!isMounted) return;
        setErrorToastMessage("Could not load settings. Run create_platform_settings.sql in Supabase, then refresh.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const requestSave = () => {
    if (saving) return;

    const errors = validatePlatformSettings(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setErrorToastMessage(Object.values(errors)[0]);
      return;
    }

    setShowSaveConfirm(true);
  };

  const handleSave = async () => {
    if (saving) return;

    setShowSaveConfirm(false);
    setSaving(true);
    setToastMessage("");
    setErrorToastMessage("");

    try {
      const user = await getAdminUser();
      const saved = await savePlatformSettings(form, user?.id);
      setForm(saved);
      setToastMessage("Settings saved.");
    } catch (error) {
      console.error("Failed to save platform settings:", error);
      if (error?.fieldErrors) {
        setFieldErrors(error.fieldErrors);
      }
      setErrorToastMessage(
        error?.message || "Could not save settings. Confirm the platform_settings table exists and you are signed in as admin."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#f9f9f9] min-h-screen">
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <SuccessToast message={toastMessage} />
        <SuccessToast message={errorToastMessage} variant="error" />

        <div className="max-w-7xl mx-auto min-w-0 text-left">
          <h1 className="text-2xl md:text-[26px] font-black text-gray-900 tracking-tight uppercase italic mb-6">
            Settings
          </h1>

          {loading ? (
            <p className="text-sm text-gray-500">Loading settings...</p>
          ) : (
            <div className="max-w-4xl space-y-4">
              <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
                    <Building2 size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-black uppercase tracking-wide text-gray-900">General</h2>
                    <p className="mt-0.5 text-xs text-gray-500">Public organization contact shown in the site footer.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>Organization Name</FieldLabel>
                    <input
                      value={form.organizationName}
                      onChange={(event) => updateField("organizationName", event.target.value)}
                      className={inputClass}
                    />
                    {fieldErrors.organizationName ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.organizationName}</p>
                    ) : null}
                  </div>
                  <div>
                    <FieldLabel>Email</FieldLabel>
                    <input
                      type="email"
                      value={form.organizationEmail}
                      onChange={(event) => updateField("organizationEmail", event.target.value)}
                      className={inputClass}
                    />
                    {fieldErrors.organizationEmail ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.organizationEmail}</p>
                    ) : null}
                  </div>
                  <div>
                    <FieldLabel>Phone</FieldLabel>
                    <input
                      type="tel"
                      value={form.organizationPhone}
                      onChange={(event) => updateField("organizationPhone", event.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <FieldLabel>Website</FieldLabel>
                    <input
                      type="url"
                      value={form.organizationWebsite}
                      onChange={(event) => updateField("organizationWebsite", event.target.value)}
                      className={inputClass}
                    />
                    {fieldErrors.organizationWebsite ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.organizationWebsite}</p>
                    ) : null}
                  </div>
                  <div>
                    <FieldLabel>Facebook URL</FieldLabel>
                    <input
                      type="url"
                      value={form.facebookUrl}
                      onChange={(event) => updateField("facebookUrl", event.target.value)}
                      placeholder="https://facebook.com/..."
                      className={inputClass}
                    />
                    {fieldErrors.facebookUrl ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.facebookUrl}</p>
                    ) : null}
                  </div>
                  <div>
                    <FieldLabel>Instagram URL</FieldLabel>
                    <input
                      type="url"
                      value={form.instagramUrl}
                      onChange={(event) => updateField("instagramUrl", event.target.value)}
                      placeholder="https://instagram.com/..."
                      className={inputClass}
                    />
                    {fieldErrors.instagramUrl ? (
                      <p className="mt-1 text-xs text-red-600">{fieldErrors.instagramUrl}</p>
                    ) : null}
                  </div>
                  <div>
                    <FieldLabel>Timezone</FieldLabel>
                    <input value="Asia/Manila" readOnly className={readOnlyClass} />
                    <p className="mt-1 text-xs text-gray-500">Locked to event and checkout dates.</p>
                  </div>
                  <div>
                    <FieldLabel>Currency</FieldLabel>
                    <input value="PHP" readOnly className={readOnlyClass} />
                    <p className="mt-1 text-xs text-gray-500">Locked to PayMongo checkout.</p>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
                    <Wrench size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-black uppercase tracking-wide text-gray-900">Maintenance</h2>
                    <p className="mt-0.5 text-xs text-gray-500">Temporarily hide the public site. Admin pages stay available.</p>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.maintenanceMode}
                    onChange={(event) => updateField("maintenanceMode", event.target.checked)}
                    className="mt-0.5 w-4 h-4 text-brand-maroon rounded"
                  />
                  <span className="text-sm text-gray-800">Enable maintenance mode</span>
                </label>

                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    When enabled, the public site is unavailable to visitors. Admins can still use the portal and browse the site.
                  </p>
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
                    <LinkIcon size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-black uppercase tracking-wide text-gray-900">Related</h2>
                    <p className="mt-0.5 text-xs text-gray-500">These stay on their own pages.</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <Link to="/admin/profile" className="text-brand-maroon font-semibold hover:underline">
                    Admin profile
                  </Link>
                  <Link to="/admin/accounts" className="text-brand-maroon font-semibold hover:underline">
                    Accounts
                  </Link>
                  <Link to="/admin/featured" className="text-brand-maroon font-semibold hover:underline">
                    Featured events
                  </Link>
                </div>
              </section>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={requestSave}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 bg-brand-maroon text-white px-4 py-2.5 rounded-md text-xs font-bold uppercase hover:bg-red-700 disabled:opacity-60"
                >
                  <Save size={14} />
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}

          {showSaveConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="save-settings-title"
                className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6"
              >
                <h3 id="save-settings-title" className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                  Save Changes?
                </h3>

                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to save these platform settings?
                </p>

                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setShowSaveConfirm(false)}
                    disabled={saving}
                    className="px-4 py-2 min-h-11 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 min-h-11 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
